import { useEffect, useRef, useState } from 'react';
import * as v from 'valibot';

import { Button, DataTable, Icon, Modal, Pagination, Radio, RadioGroup, Spinner, Text } from '@nvision/ui';

import { NoData } from '@/modules/errors';

import { usePostals } from '../../../hooks';
import {
  type PostalSearchFormValues,
  type Postals,
  type PostalsParams,
  postalSearchParamsSchema,
} from '../../../models';
import * as countriesService from '../../../services/countries.service';
import type { SearchFormRef } from '../../products';
import { PostalSearchForm } from '../postal-search-form';

import styles from './postal-search-modal.module.css';

const getDefaultSearchParams = (): PostalsParams => v.getDefaults(postalSearchParamsSchema) as unknown as PostalsParams;

type PostalSearchContext = 'default' | 'origin' | 'destination';

interface PostalSearchModalProps {
  defaultSelection?: Postals;
  defaultValues?: PostalSearchFormValues;
  isOpen: boolean;
  searchContext?: PostalSearchContext;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange: (values: Postals) => void;
}

interface SavedSearch {
  params: PostalsParams;
  sorting: boolean;
}

export const PostalSearchModal: React.FC<PostalSearchModalProps> = ({
  defaultSelection,
  defaultValues,
  isOpen = false,
  searchContext = 'default',
  setIsOpen,
  onSelectionChange,
}) => {
  const [params, setParams] = useState(getDefaultSearchParams);
  const [sorting, setSorting] = useState(false);
  const [selectedPostals, setSelectedPostals] = useState<Postals>([]);

  const { data: postals, isFetching } = usePostals(params);

  const searchFormRef = useRef<SearchFormRef>({} as SearchFormRef);
  const savedSearchesRef = useRef<Partial<Record<PostalSearchContext, SavedSearch>>>({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedPostals(defaultSelection ?? []);

    const savedSearch = savedSearchesRef.current[searchContext];
    setParams(savedSearch?.params ?? getDefaultSearchParams());
    setSorting(savedSearch?.sorting ?? false);
  }, [defaultSelection, isOpen, searchContext]);

  const reset = () => {
    setSelectedPostals([]);
    setParams(getDefaultSearchParams());
    setSorting(false);
  };

  const close = () => {
    searchFormRef.current?.reset();
    setSelectedPostals([]);
    setIsOpen(false);
  };

  const submit = (values: PostalSearchFormValues) => {
    setParams((prevParams) => ({
      ...prevParams,
      city: values.additional.city,
      country: values.country.id,
      offset: 0,
      portPostal: values.additional.portPostal,
      sorting: `cityName ${sorting ? 'desc' : 'asc'}`,
      state: values.additional.state?.id,
    }));
  };

  const submitSelection = () => {
    savedSearchesRef.current[searchContext] = { params, sorting };
    onSelectionChange(selectedPostals);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(value) => setIsOpen(value)} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <Text size="xlg" weight="semibold">
          Postal Search
        </Text>
        <Icon name="x" onClick={close} cursor="pointer" />
      </Modal.Header>
      <Modal.Content className={styles.content}>
        {isOpen && (
          <PostalSearchForm
            defaultValues={defaultValues}
            formRef={searchFormRef}
            handleSubmit={submit}
            handleReset={reset}
          />
        )}
        {isFetching ? (
          <div className={`${styles.tableContainer} ${styles.loader}`}>
            <Spinner size="lg" />
          </div>
        ) : postals?.data.length ? (
          <div>
            <div className={styles.total}>
              {postals.total === 1 ? (
                <>
                  <Text weight="bold">{`${postals.total} postal `}</Text>is found
                </>
              ) : (
                <>
                  <Text weight="bold">{`${postals.total} postals `}</Text>are found
                </>
              )}
            </div>
            <div className={styles.tableContainer}>
              <DataTable
                columns={[
                  {
                    accessorKey: 'id',
                    cell: (props) => (
                      <RadioGroup
                        value={selectedPostals[0]?.id === props.row.original.id ? props.row.original.id : ''}
                        onChange={() => setSelectedPostals([props.row.original])}
                        className={styles.radio}
                      >
                        <Radio value={props.row.original.id} className={styles.radio} />
                      </RadioGroup>
                    ),
                    enableSorting: false,
                    header: '',
                    maxSize: 0,
                  },
                  {
                    accessorKey: 'cityName',
                    cell: (props) => props.row.original.cityName,
                    header: 'City',
                    sortingFn: () => 0,
                  },
                  {
                    accessorKey: 'stateCode',
                    cell: (props) => props.row.original.stateCode,
                    enableSorting: false,
                    header: 'State/ Province',
                  },
                  {
                    accessorKey: 'postalCode',
                    cell: (props) => props.row.original.postalCode,
                    enableSorting: false,
                    header: 'Postal Code',
                  },
                  {
                    accessorKey: 'countryCode',
                    cell: (props) => {
                      const countryCode = props.row.original.countryCode ?? '';
                      return countriesService.getAll()?.map[countryCode]?.label ?? countryCode;
                    },
                    enableSorting: false,
                    header: 'Country',
                  },
                ]}
                data={postals.data}
                isLoading={isFetching}
                page={(params.offset ?? 0) / (params.limit ?? 10) + 1}
                limit={params.limit}
                totalItems={postals.total}
                sorting={[
                  {
                    desc: sorting,
                    id: 'cityName',
                  },
                ]}
                onSortingChange={() => {
                  const isDescending = !sorting;
                  setSorting(isDescending);
                  setParams((prev) => ({
                    ...prev,
                    offset: 0,
                    sorting: `cityName ${isDescending ? 'desc' : 'asc'}`,
                  }));
                }}
              />
            </div>

            <Pagination
              className={styles.pagination}
              totalItems={postals.total}
              limit={params.limit}
              page={(params.offset ?? 0) / (params.limit ?? 10) + 1}
              onPageChange={(page: number) => {
                setParams((prev) => ({ ...prev, offset: (page - 1) * (prev.limit ?? 10) }));
              }}
              onOffsetChange={(offset: number) => setParams((prev) => ({ ...prev, offset }))}
              onLimitChange={(limit: number) => setParams((prev) => ({ ...prev, limit, offset: 0 }))}
            />
          </div>
        ) : (
          <NoData
            className={styles.noData}
            description="No data to display. Please select a Country and at least one of the following fields: City, State/Province, or Port
Postal."
          />
        )}
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button outline secondary onClick={close}>
          Cancel
        </Button>
        <Button isDisabled={selectedPostals.length === 0} onClick={submitSelection}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
